import { FirebaseMessaging } from '@capacitor-firebase/messaging';

import {
  debounce,
  isAndroid,
  isNativeDevice,
  logError,
  TIME_IN_MS,
} from '@utils';

import { Notification } from './push-notification-service.types';
import { ANDROID_CHANNELS } from './push-notifications-service.constant';

export class PushNotificationsService {
  private static isSubscribed = false;

  private static isServiceAvailable() {
    return isNativeDevice;
  }

  private static async requestPermissions() {
    const result = await FirebaseMessaging.requestPermissions();
    return result.receive === 'granted';
  }

  private static async checkPermissions() {
    const result = await FirebaseMessaging.checkPermissions();
    return result.receive === 'granted';
  }

  private static async createAndroidChannels() {
    if (!isAndroid()) {
      return;
    }

    try {
      for (const androidChannel of ANDROID_CHANNELS) {
        await FirebaseMessaging.createChannel(androidChannel);
      }
    } catch (error) {
      logError(
        '🚀 ~ PushNotificationsService ~ createAndroidChannels ~ error:' +
          String(error)
      );
    }
  }

  static async getToken() {
    if (!this.isServiceAvailable()) return undefined;

    const isGranted = await this.checkPermissions();
    if (!isGranted) return undefined;

    const result = await FirebaseMessaging.getToken();

    return result.token;
  }

  static async init() {
    try {
      if (!this.isServiceAvailable()) return undefined;

      const isGranted = await this.requestPermissions();
      if (!isGranted) return undefined;

      await this.createAndroidChannels();

      const token = await this.getToken();
      return token;
    } catch (error) {
      logError(`🚀 ~ PushNotificationsService ~ init ~ error:${String(error)}`);
      throw error;
    }
  }

  static async subscribe(
    handleNotificationClicked: (notification: Notification) => void,
    handleTokenGenerated: (token: string) => void,
    handleNotificationReceived: (notification: Notification) => void
  ) {
    if (!this.isServiceAvailable()) return;

    if (this.isSubscribed) return;

    const isGranted = await this.checkPermissions();
    if (!isGranted) return;

    this.isSubscribed = true;

    const debouncedHandleTokenGenerated = debounce(
      handleTokenGenerated,
      TIME_IN_MS.FIVE_SECONDS
    );

    await FirebaseMessaging.addListener('tokenReceived', tokenReceivedEvent => {
      debouncedHandleTokenGenerated(tokenReceivedEvent.token);
    });

    await FirebaseMessaging.addListener(
      'notificationReceived',
      notificationEvent => {
        const { notification } = notificationEvent;
        handleNotificationReceived(notification as Notification);
      }
    );

    await FirebaseMessaging.addListener(
      'notificationActionPerformed',
      action => {
        const { notification } = action;

        handleNotificationClicked(notification as Notification);
      }
    );
  }

  static async unsubscribe() {
    if (!this.isSubscribed) return;

    this.isSubscribed = false;

    await FirebaseMessaging.removeAllListeners();
    await FirebaseMessaging.deleteToken();
  }

  static async clearNotificationsBadgeCount() {
    if (this.isServiceAvailable() && this.isSubscribed) {
      try {
        await FirebaseMessaging.removeAllDeliveredNotifications();
      } catch (error) {
        logError(
          `Failed to removeAllDeliveredNotifications error:${String(error)}`
        );
      }
    }
  }
}
